import {
    addDays,
    addMinutes,
    addWeeks,
    endOfWeek,
    format,
    formatISO,
    getISOWeekYear,
    getWeek,
    isBefore,
    isThisWeek,
    isThisYear,
    isToday,
    isTomorrow,
    isYesterday,
    startOfTomorrow,
    startOfWeek,
    subWeeks,
} from 'date-fns';
import * as MathUtils from './math';
import { IAccountSettings } from 'models/Account';

export enum TimeInMilliseconds {
    ONE_MINUTE = 60000,
    THREE_MINUTES = 180000,
    FIVE_MINUTES = 300000,
    ONE_HOUR = 3600000,
    ONE_DAY = 86400000,
}

/**
 * These functions seem like unnecessary abstraction, but they let us intercept
 * Date calls in tests, so we can mock the system time.  Parse hates when we
 * use jest.useFakeTimers() so we have to do this instead.
 *
 * @returns
 */
export function makeDate(...args: (number | string)[]): Date {
    if (args.length === 0) {
        return new Date();
    } else if (args.length === 1) {
        return new Date(args[0]);
    } else {
        return new (Date.bind.apply(Date, [null, ...args]))();
    }
}

/**
 * These functions seem like unnecessary abstraction, but they let us intercept
 * Date calls in tests, so we can mock the system time.  Parse hates when we
 * use jest.useFakeTimers() so we have to do this instead.
 *
 * @returns
 */
export function getCurrentSystemTime(): number {
    return Date.now();
}

export function absoluteTime(time: Date | undefined | null): string {
    if (!time) {
        return '';
    }
    const options: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    };
    return time.toLocaleString('en-US', options);
}

export function localizedFullDateTime(time: Date | undefined | null): string {
    if (!time) {
        return '';
    }
    const options: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short',
    };
    return time.toLocaleString('en-US', options);
}

export function showTime(time: Date): string {
    if (!time) return 'Unknown';
    if (isToday(time)) return 'Today';
    if (isThisWeek(time)) return format(time, 'EEE');
    if (isThisYear(time)) return format(time, 'MM/dd');

    return format(time, 'MM/dd/yy');
}

export function showTimeLong(time: Date, formatString?: string): string {
    if (!time) return 'Unknown';
    if (isYesterday(time)) return 'Yesterday';
    if (isToday(time)) return 'Today';
    if (isTomorrow(time)) return 'Tomorrow';

    if (isThisWeek(time)) return format(time, 'EEEE');

    return format(time, formatString ? formatString : 'MMM do yyyy');
}

export function isWithinADay(time: Date): boolean {
    return isYesterday(time) || isToday(time) || isTomorrow(time);
}

export function showTimeCalendar(time: Date): string {
    return format(time, 'MMM d, yyyy');
}

export function ISOFromDate(time: Date): string {
    return formatISO(time);
}

export function dateFromISO(ISODate: string): Date {
    return makeDate(ISODate);
}

export function showTimeShortAbsolute(time: Date): string {
    if (!time) return 'Unknown';
    if (isToday(time)) return 'Today';

    return format(time, 'MMM d');
}

export function getUTCOffsetForQuery(date: Date): string {
    const offsetInMinutes = date.getTimezoneOffset();

    // format the offset into a string
    // For example, if you want to show the offset as "+05:30" or "-03:00"
    return format(addMinutes(date, offsetInMinutes), 'xxx');
}

export function getWeekLabels(weeksAhead: number): string[] {
    const now = new Date();
    const weekLabels = ['This Week'];
    for (let i = 1; i < weeksAhead + 1; i++) {
        const week = addWeeks(now, i);
        const start = startOfWeek(week);
        const end = endOfWeek(week);

        // hide the second month if the entire week falls within month boundaries
        if (start.getMonth() === end.getMonth()) {
            weekLabels.push(format(start, 'MMM dd') + '-' + format(end, 'dd'));
        } else {
            // show both month labels
            weekLabels.push(
                format(start, 'MMM dd') + '-' + format(end, 'MMM dd')
            );
        }
    }
    return weekLabels;
}

export type WeekMapPost = { postedAt: Date; scheduledAt: Date };
export interface WeekMapResults {
    [weekYearKey: string]: WeekMapPost[];
}

export const weekMapKeyToWeekYear = (
    key: string | number
): { week: number; year: number } => {
    const weekKey = typeof key === 'string' ? Number(key) : key;
    return { week: weekKey % 100, year: Math.floor(weekKey / 100) };
};

export const createWeekmap = ({
    lookbackWeeks = 8,
    lookaheadWeeks = 8,
    posts = undefined,
}: {
    lookbackWeeks: number;
    lookaheadWeeks: number;
    posts?: WeekMapPost[];
}): WeekMapResults => {
    const today = new Date();
    const weekMap: WeekMapResults = {};

    // Populate past weeks
    for (let i = lookbackWeeks; i >= 1; i--) {
        const priorWeek = subWeeks(today, i);
        const week = MathUtils.twoDigit(getWeek(priorWeek));
        const year = getISOWeekYear(priorWeek); // Updated here
        weekMap[`${year}${week}`] = [];
    }

    // Current week
    const thisWeek = MathUtils.twoDigit(getWeek(today));
    const thisYear = getISOWeekYear(today); // Updated here
    weekMap[`${thisYear}${thisWeek}`] = [];

    // Populate future weeks
    for (let i = 1; i <= lookaheadWeeks; i++) {
        const nextWeek = addWeeks(today, i);
        const week = MathUtils.twoDigit(getWeek(nextWeek));
        const year = getISOWeekYear(nextWeek); // Updated here
        weekMap[`${year}${week}`] = [];
    }

    // Assign posts to weeks
    if (posts) {
        posts.forEach((post) => {
            const postDate = post.postedAt || post.scheduledAt;
            const weekNum = MathUtils.twoDigit(getWeek(postDate));
            const weekYear = getISOWeekYear(postDate); // Updated here
            const weekKey = Number(`${weekYear}${weekNum}`);

            if (weekMap[weekKey]) {
                weekMap[weekKey].push(post);
            }
        });
    }

    return weekMap;
};

export function getNextAvailableScheduledTime(): Date {
    const earliestScheduledTime = new Date();
    earliestScheduledTime.setHours(new Date().getHours() + 1, 0, 0, 0);
    // if it's earlier than 9pm we can schedule for today
    if (earliestScheduledTime.getHours() < 21) {
        return earliestScheduledTime;
    }

    // too late in the day to post, the user will need to wait until tomorrow morning
    const tomorrow = addDays(new Date(), 1);
    tomorrow.setHours(7, 0);
    return tomorrow;
}

// Only used on future dates, not today.
export function getDefaultPostTimeOfDay(
    date: Date,
    settings: IAccountSettings
): Date {
    date.setHours(settings.defaultPostScheduleHour, 0);
    return date;
}

export const DAY_SCHEDULE_3 = [1, 3, 5]; // Post on Monday, Wednesday & Friday
export const DAY_SCHEDULE_5 = [1, 2, 3, 4, 5]; // Post Monday through Friday

/**
 * This function gets the remaining default "slots" in the week based
 * on the number of posts per week in account settings.
 *
 * @param w the week of year
 * @param y the year
 * @param defaultNumberOfPostsPerWeek 3 or 5
 * @param posts the posts already posted for this week
 */
export function getRemainingPostDaysInWeek(
    w: number,
    y: number,
    defaultNumberOfPostsPerWeek: number,
    posts: WeekMapPost[]
): number[] {
    const schedule: number[] =
        defaultNumberOfPostsPerWeek === 3 ? DAY_SCHEDULE_3 : DAY_SCHEDULE_5;

    const currentScheduled: number[] = posts.map((post) =>
        (post.postedAt || post.scheduledAt).getDay()
    );

    const remainingSchedule = schedule.filter((d) => {
        const index = currentScheduled.indexOf(d);
        if (index >= 0) {
            currentScheduled.splice(index, 1);
            return false;
        } else {
            return true;
        }
    });

    if (currentScheduled.length > 0) {
        // Scheduled on days not default, just take out of the list from begining.
        remainingSchedule.splice(0, currentScheduled.length);
    }

    // Special case for any day before start of tomorrow.
    const tomorrow = startOfTomorrow();
    const thisWeek = startOfWeek(getDateOfWeek(w, y));

    return remainingSchedule.filter((day) =>
        isBefore(tomorrow, addDays(thisWeek, day))
    );
}

export function getDefaultPostDateOfWeek(
    w: number,
    y: number,
    settings: IAccountSettings
): Date {
    let date = startOfWeek(getDateOfWeek(w, y));

    date = addDays(date, settings.defaultPostScheduleDayOfWeek);
    date.setHours(settings.defaultPostScheduleHour, 0);
    const nextAvailableScheduledTime = getNextAvailableScheduledTime();
    // don't let the user schedule anything in the past
    const time = Math.max(date.getTime(), nextAvailableScheduledTime.getTime());
    return new Date(time);
}

export function getDateOfWeek(w: number, y: number): Date {
    const d = 1 + (w - 1) * 7; // 1st of January + 7 days for each week

    return new Date(y, 0, d);
}

export function exifDate(
    dateInput?: string | Record<string, unknown>
): Date | undefined {
    if (!dateInput) {
        return undefined;
    }
    let dateString: string;
    if (typeof dateInput === 'object') {
        dateString = Object.values(dateInput).join();
    } else {
        dateString = dateInput;
    }

    const [year, month, date, hour, min, sec] = dateString.split(/\D/);
    return new Date(
        Number(year),
        Number(month) - 1,
        Number(date),
        Number(hour),
        Number(min),
        Number(sec)
    );
}

export function exifFormat(value: Date): string {
    if (!value) {
        return '';
    }

    return format(value, 'yyyy:MM:dd HH:mm:ss');
}

export function formatDateRange(startDate?: Date, endDate?: Date): string {
    const startFormat = 'MMM d';
    const endFormatSameMonth = 'd, yyyy';
    const endFormatDifferentMonth = 'MMM d, yyyy';
    const endFormatDifferentYear = 'MMM d, yyyy';

    if (!startDate) {
        return '';
    }

    if (endDate) {
        if (startDate.getFullYear() !== endDate.getFullYear()) {
            return `${format(
                startDate,
                startFormat
            )}, ${startDate.getFullYear()}–${format(
                endDate,
                endFormatDifferentYear
            )}`;
        } else if (startDate.getMonth() !== endDate.getMonth()) {
            return `${format(startDate, startFormat)}–${format(
                endDate,
                endFormatDifferentMonth
            )}`;
        } else if (startDate.getDate() !== endDate.getDate()) {
            return `${format(startDate, startFormat)}–${format(
                endDate,
                endFormatSameMonth
            )}`;
        } else {
            return format(startDate, `${startFormat}, yyyy`);
        }
    } else {
        return format(startDate, `${startFormat}, yyyy`);
    }
}

export const MM_DD_YYYY = new RegExp(
    '^(0[1-9]|1[0-2])\\/(0[1-9]|[12][0-9]|3[01])\\/\\d{4}$'
);

export const isValidMMDDYYYY = (dateString: string): boolean => {
    return MM_DD_YYYY.test(dateString);
};

export const isWithinBusinessHours = () => {
    const now = new Date();
    const hour = now.getHours();
    return hour >= 9 && hour < 17; // 9:00 AM to 4:59 PM
};

export function msToMinutesAndSeconds(milliseconds: number): string {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    const minLabel = minutes === 1 ? 'minute' : 'minutes';
    const secLabel = seconds === 1 ? 'second' : 'seconds';
    return minutes > 0
        ? `${minutes} ${minLabel} ${seconds} ${secLabel}`
        : `${seconds} ${secLabel}`;
}
